import $api from "@/shared/http";
import { toApiError } from "@/shared/http/types";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import type {
  CreateMarginalityCombinationRequest,
  MarginalityCategoriesResponse,
  MarginalityColorsResponse,
  MarginalityCombinationsResponse,
  MarginalityDesignsResponse,
  MarginalityDiametersResponse,
  MarginalitySliceState,
  UpdateMarginalityCombinationRequest,
} from "./types";

const initialState: MarginalitySliceState = {
  currentCombinations: [],
  historyCombinations: [],
  categories: [],
  colors: [],
  isLoading: false,
  isMiniLoading: false,
  error: null,
  diameters: [],
  designs: [],
};

// Запросы
export const fetchCurrentCombinations = createAsyncThunk<
  MarginalityCombinationsResponse,
  void,
  { rejectValue: string }
>("marginality/fetchCurrentCombinations", async (_, { rejectWithValue }) => {
  try {
    const response = await $api.get<MarginalityCombinationsResponse>(
      "/marginality/combination/current",
    );
    return response.data;
  } catch (error) {
    const apiError = toApiError(error);
    return rejectWithValue(apiError.message);
  }
});

export const fetchHistoryCombinations = createAsyncThunk<
  MarginalityCombinationsResponse,
  void,
  { rejectValue: string }
>("marginality/fetchHistoryCombinations", async (_, { rejectWithValue }) => {
  try {
    const response = await $api.get<MarginalityCombinationsResponse>(
      "/marginality/combination/history",
    );
    return response.data;
  } catch (error) {
    const apiError = toApiError(error);
    return rejectWithValue(apiError.message);
  }
});

export const fetchDiameters = createAsyncThunk<
  MarginalityDiametersResponse,
  void,
  { rejectValue: string }
>("marginality/fetchDiameters", async (_, { rejectWithValue }) => {
  try {
    const response = await $api.get<MarginalityDiametersResponse>(
      "/marginality/diameters",
    );
    return response.data;
  } catch (error) {
    const apiError = toApiError(error);
    return rejectWithValue(apiError.message);
  }
});

export const fetchDesigns = createAsyncThunk<
  MarginalityDesignsResponse,
  void,
  { rejectValue: string }
>("marginality/fetchDesigns", async (_, { rejectWithValue }) => {
  try {
    const response = await $api.get<MarginalityDesignsResponse>(
      "/marginality/designs",
    );
    return response.data;
  } catch (error) {
    const apiError = toApiError(error);
    return rejectWithValue(apiError.message);
  }
});

export const fetchmarginalityCategoryIds = createAsyncThunk<
  MarginalityCategoriesResponse,
  void,
  { rejectValue: string }
>("marginality/fetchCategories", async (_, { rejectWithValue }) => {
  try {
    const response = await $api.get<MarginalityCategoriesResponse>(
      "/marginality/categories",
    );
    return response.data;
  } catch (error) {
    const apiError = toApiError(error);
    return rejectWithValue(apiError.message);
  }
});

export const createMarginalityCombination = createAsyncThunk<
  void,
  CreateMarginalityCombinationRequest,
  { rejectValue: string }
>("marginality/createCombination", async (body, { rejectWithValue }) => {
  try {
    await $api.post("/marginality/combination", body);
  } catch (error) {
    const apiError = toApiError(error);
    return rejectWithValue(apiError.message);
  }
});

export const updateMarginalityCombination = createAsyncThunk<
  void,
  UpdateMarginalityCombinationRequest,
  { rejectValue: string }
>("marginality/updateCombination", async (body, { rejectWithValue }) => {
  try {
    await $api.put("/marginality/combination", body);
  } catch (error) {
    const apiError = toApiError(error);
    return rejectWithValue(apiError.message);
  }
});

export const deleteMarginalityCombination = createAsyncThunk<
  void,
  number,
  { rejectValue: string }
>("marginality/deleteCombination", async (id, { rejectWithValue }) => {
  try {
    await $api.delete(`/marginality/combination/${id}`);
  } catch (error) {
    const apiError = toApiError(error);
    return rejectWithValue(apiError.message);
  }
});

export const fetchMarginalityColors = createAsyncThunk<
  MarginalityColorsResponse,
  void,
  { rejectValue: string }
>("marginality/fetchMarginalityColors", async (_, { rejectWithValue }) => {
  try {
    const response = await $api.get<MarginalityColorsResponse>(
      "/marginality/colors",
    );
    return response.data;
  } catch (error) {
    const apiError = toApiError(error);
    return rejectWithValue(apiError.message);
  }
});

export const marginalitySlice = createSlice({
  name: "marginality",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers(builder) {
    // Загрузка текущих комбинаций
    builder.addCase(fetchCurrentCombinations.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(fetchCurrentCombinations.fulfilled, (state, action) => {
      state.isLoading = false;
      state.currentCombinations = action.payload.items;
    });
    builder.addCase(fetchCurrentCombinations.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });
    // Загрузка текущих диаметров
    builder.addCase(fetchDiameters.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(fetchDiameters.fulfilled, (state, action) => {
      state.isLoading = false;
      state.diameters = action.payload.items;
    });
    builder.addCase(fetchDiameters.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });
    // Загрузка текущих дизайнов
    builder.addCase(fetchDesigns.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(fetchDesigns.fulfilled, (state, action) => {
      state.isLoading = false;
      state.designs = action.payload.items;
    });
    builder.addCase(fetchDesigns.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });

    // Загрузка исторических комбинаций
    builder.addCase(fetchHistoryCombinations.pending, (state) => {
      state.isMiniLoading = true;
      state.error = null;
    });
    builder.addCase(fetchHistoryCombinations.fulfilled, (state, action) => {
      state.isMiniLoading = false;
      state.historyCombinations = action.payload.items;
    });
    builder.addCase(fetchHistoryCombinations.rejected, (state, action) => {
      state.isMiniLoading = false;
      state.error = action.payload as string;
    });

    // Загрузка категорий маржинальности
    builder.addCase(fetchmarginalityCategoryIds.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(fetchmarginalityCategoryIds.fulfilled, (state, action) => {
      state.isLoading = false;
      state.categories = action.payload.items || [];
    });
    builder.addCase(fetchmarginalityCategoryIds.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });

    // Загрузка цветов маржинальности
    builder.addCase(fetchMarginalityColors.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(fetchMarginalityColors.fulfilled, (state, action) => {
      state.isLoading = false;
      state.colors = action.payload.items || [];
    });
    builder.addCase(fetchMarginalityColors.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });

    // Создание комбинации маржинальности
    builder.addCase(createMarginalityCombination.pending, (state) => {
      state.isMiniLoading = true;
      state.error = null;
    });
    builder.addCase(createMarginalityCombination.fulfilled, (state) => {
      state.isMiniLoading = false;
      state.error = null;
    });
    builder.addCase(createMarginalityCombination.rejected, (state, action) => {
      state.isMiniLoading = false;
      state.error = action.payload as string;
    });

    // Обновление комбинации маржинальности
    builder.addCase(updateMarginalityCombination.pending, (state) => {
      state.isMiniLoading = true;
      state.error = null;
    });
    builder.addCase(updateMarginalityCombination.fulfilled, (state) => {
      state.isMiniLoading = false;
      state.error = null;
    });
    builder.addCase(updateMarginalityCombination.rejected, (state, action) => {
      state.isMiniLoading = false;
      state.error = action.payload as string;
    });

    // Удаление комбинации маржинальности
    builder.addCase(deleteMarginalityCombination.pending, (state) => {
      state.isMiniLoading = true;
      state.error = null;
    });
    builder.addCase(deleteMarginalityCombination.fulfilled, (state) => {
      state.isMiniLoading = false;
      state.error = null;
    });
    builder.addCase(deleteMarginalityCombination.rejected, (state, action) => {
      state.isMiniLoading = false;
      state.error = action.payload as string;
    });
  },
});
export const { clearError } = marginalitySlice.actions;
export default marginalitySlice.reducer;
